import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const FALLBACK_SRC = '/images/placeholder-cover.svg';

const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className,
  style,
  priority = false,
  sizes,
  defer = false, // NEW
  onError: onErrorProp,
  ...props
}) => {
  // If defer -> only render image when it is in view
  const ref = useRef(null);
  const [inView, setInView] = useState(!defer);
  const [failed, setFailed] = useState(false);

  // Reset fail state when src changes so a new URL gets a fresh attempt.
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const effectiveSrc = failed ? FALLBACK_SRC : src;

  // Safe regex pattern for SVG detection - escaped special chars
  // Using character class with bounded quantifier to avoid ReDoS vulnerability
  // eslint-disable-next-line security/detect-unsafe-regex
  const SVG_PATTERN = /\.svg(\?[a-zA-Z0-9&=]{0,100})?$/i;
  const isSVG = effectiveSrc ? SVG_PATTERN.test(effectiveSrc) : false;

  useEffect(() => {
    if (!defer) {
      return undefined;
    }
    if (!ref.current) {
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' }, // start loading slightly before visible
    );

    io.observe(ref.current);
    return () => {
      io.disconnect();
    };
  }, [defer]);

  // placeholder wrapper always reserves space (prevents CLS)
  const wrapperStyle = useMemo(
    () => ({
      display: 'inline-block',
      width,
      height,
    }),
    [width, height],
  );

  const handleError = useCallback(
    (event) => {
      setFailed((prev) => prev || true);
      if (typeof onErrorProp === 'function') {
        onErrorProp(event);
      }
    },
    [onErrorProp],
  );

  if (!effectiveSrc || effectiveSrc.trim() === '') {
    return null;
  }

  return (
    <span ref={ref} style={wrapperStyle}>
      {inView ? (
        <Image
          src={effectiveSrc}
          alt={alt}
          width={width}
          height={height}
          className={className}
          style={style}
          priority={priority}
          sizes={sizes}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          // keep unoptimized for svg
          unoptimized={isSVG}
          onError={handleError}
          {...props}
        />
      ) : null}
    </span>
  );
};

export default OptimizedImage;
